import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages bulk pricing for a product.
 *
 * @typedef {Object} Refs
 * @property {HTMLScriptElement} bulkPricingData
 *
 * @extends {Component<Refs>}
 */
export default class BulkPricingBreakdown extends Component {
  /** @type {((event: Event) => void) | null} */
  #boundOnQuantityUpdate = null;

  static observedAttributes = ['data-variant-id', 'data-unit'];

  connectedCallback() {
    super.connectedCallback();
    this.#setValues();
    this.#generateMessaging();
    this.#bindListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unbindListeners();
  }

  /**
   * Called by the browser when an observed attribute changes (including via morph's copyAttributes).
   */
  attributeChangedCallback() {
    if (!this.isConnected) return;
    this.#setValues();
    this.#generateMessaging();
  }

  #bindListeners() {
    this.#boundOnQuantityUpdate = this.#onQuantityUpdate.bind(this);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#boundOnQuantityUpdate);
  }

  #unbindListeners() {
    if (this.#boundOnQuantityUpdate) {
      document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#boundOnQuantityUpdate);
      this.#boundOnQuantityUpdate = null;
    }
  }

  #setValues() {
    this.variantId = this.dataset.variantId ? parseInt(this.dataset.variantId, 10) : null;
    this.unitOfMeasurement = this.dataset.unit;
    try {
      this.bulkPricingData = this.refs.bulkPricingData && this.refs.bulkPricingData instanceof HTMLElement ? JSON.parse(this.refs.bulkPricingData.textContent) : null;
    } catch {
      this.bulkPricingData = null;
    }
    this.currentQuantity = 1;
  }

  /**
   * Handles the quantity update event.
   * @param {Event} event - The quantity update event.
   */
  #onQuantityUpdate(event) {
    if(!this.variantId || !this.unitOfMeasurement || !this.bulkPricingData) return;
    const { detail } = /** @type {import('@theme/events').QuantitySelectorUpdateEvent} */ (event);
    if (detail?.variantId !== this.variantId) return;
    this.currentQuantity = detail.quantity;
    this.#generateMessaging();
  }

  /**
   * Generates approximate price in total based on unit price and quantity thresholds
   */
  #generateMessaging() {
    if(!this.variantId || !this.unitOfMeasurement || !this.bulkPricingData || !this.currentQuantity) return;

    /** @type {{ quantity: number, price: number, uom: string } | null} */
    let matched = null;

    for (const tier of this.bulkPricingData) {
      if (this.currentQuantity >= tier.quantity) {
        matched = tier;
      }
    }

    if(!matched) {
        this.classList.add("hidden");
        return;
    }

    const currency = window.Shopify?.currency?.active || null;
    if(!currency) {
        this.classList.add("hidden");
        return;
    }
    const total = matched.price * this.currentQuantity;
    const formatOpts = /** @type {Intl.NumberFormatOptions} */ ({ style: 'currency', currency, currencyDisplay: 'narrowSymbol' });
    const formattedTotal = new Intl.NumberFormat(undefined, formatOpts).format(total / 100);
    const formattedUnitPrice = new Intl.NumberFormat(undefined, formatOpts).format(matched.price / 100);

    const container = this.refs.bulkPricingContent;
    if (!container || container instanceof Array) {
        this.classList.add("hidden");
        return;
    }

    const qty = document.createElement('span');
    qty.className = 'bulk-pricing-quantity';
    qty.textContent = `${this.currentQuantity}${this.unitOfMeasurement}`;

    const seeds = document.createElement('span');
    seeds.className = 'bulk-pricing-seeds';
    seeds.textContent = ' seeds ';

    const separator = document.createElement('span');
    separator.className = 'bulk-pricing-separator';
    separator.textContent = ' x ';

    const unitPrice = document.createElement('span');
    unitPrice.className = 'bulk-pricing-unit-price';
    unitPrice.textContent = formattedUnitPrice;

    const equals = document.createElement('span');
    equals.className = 'bulk-pricing-equals';
    equals.textContent = ' = ';

    const totalEl = document.createElement('span');
    totalEl.className = 'bulk-pricing-total';
    totalEl.textContent = formattedTotal;

    container.textContent = '';
    container.append(qty, seeds, separator, unitPrice, equals, totalEl);
    this.classList.remove("hidden");
  }
}

if (!customElements.get('bulk-pricing-breakdown')) {
  customElements.define('bulk-pricing-breakdown', BulkPricingBreakdown);
}
